import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useActiveAccount, useActiveWallet, useDisconnect } from 'thirdweb/react';
import { getUserPreferences, updateUserPreferences, syncOnChainPositions, getNotifications, markNotificationsRead } from '../lib/api';

const WalletContext = createContext();
const MANUAL_KEY = 'g12_wallet_manual';

export function WalletProvider({ children }) {
  // ── Thirdweb real wallet ──────────────────────────────────────────────
  const thirdwebAccount = useActiveAccount();   // { address, ... } | undefined
  const activeWallet    = useActiveWallet();
  const { disconnect: thirdwebDisconnect } = useDisconnect();

  // ── Manual fallback — also used as cache for Thirdweb address ────────
  // Survives page refresh so walletAddress is available immediately
  const [manualWallet, setManualWallet] = useState(() => localStorage.getItem(MANUAL_KEY));

  // Track whether manualWallet was set by a Thirdweb connection (vs manual entry)
  const thirdwebCached = useRef(false);

  // When a real Thirdweb wallet connects, persist its address as cache.
  // When it disconnects (address goes away), clear the cache so walletAddress becomes null.
  useEffect(() => {
    if (thirdwebAccount?.address) {
      const addr = thirdwebAccount.address.toLowerCase();
      localStorage.setItem(MANUAL_KEY, addr);
      setManualWallet(addr);
      thirdwebCached.current = true;
    } else if (thirdwebCached.current) {
      // Thirdweb was connected and is now gone — clear the cached address
      thirdwebCached.current = false;
      localStorage.removeItem(MANUAL_KEY);
      setManualWallet(null);
    }
  }, [thirdwebAccount?.address]);


  // Resolved address — Thirdweb takes priority, falls back to cached
  const walletAddress = thirdwebAccount?.address?.toLowerCase() ?? manualWallet ?? null;

  // ── walletReady — prevents loading data with a null address ──────────
  // True immediately if an address is available (cached or live).
  // If no address yet, waits 1500ms for Thirdweb to reconnect, then gives up.
  const [walletReady, setWalletReady] = useState(false);
  useEffect(() => {
    if (walletAddress) {
      setWalletReady(true);
    } else {
      const t = setTimeout(() => setWalletReady(true), 1500);
      return () => clearTimeout(t);
    }
  }, [walletAddress]);

  // ── Preferences ────────────────────────────────────────────────────────
  const [preferences, setPreferences] = useState(null);

  // ── Notifications ─────────────────────────────────────────────────────
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (walletAddress) {
      fetchPreferences(walletAddress);
      fetchNotifications(walletAddress);
    } else {
      setPreferences(null);
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [walletAddress]);

  // Poll notifications every 60s
  useEffect(() => {
    if (!walletAddress) return;
    const interval = setInterval(() => fetchNotifications(walletAddress), 60_000);
    return () => clearInterval(interval);
  }, [walletAddress]);

  // On-chain sync — runs once when a real Thirdweb wallet connects
  useEffect(() => {
    if (thirdwebAccount?.address) {
      syncOnChainPositions(thirdwebAccount.address.toLowerCase()).catch(err =>
        console.warn('syncOnChainPositions failed:', err)
      );
    }
  }, [thirdwebAccount?.address]);

  async function fetchPreferences(address) {
    try {
      const prefs = await getUserPreferences(address);
      setPreferences(prefs);
    } catch (err) {
      console.error('Failed to load preferences:', err);
    }
  }

  async function fetchNotifications(address) {
    try {
      const data = await getNotifications(address);
      setNotifications(data.notifications ?? []);
      setUnreadCount(data.unread_count ?? 0);
    } catch (err) {
      console.error('Failed to load notifications:', err);
    }
  }

  const markAllRead = useCallback(async () => {
    if (!walletAddress) return;
    try {
      await markNotificationsRead(walletAddress, null);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark notifications read:', err);
    }
  }, [walletAddress]);

  // ── Manual connect (read-only, no signature) ──────────────────────────
  const connectManual = useCallback(async (address) => {
    const normalized = address.toLowerCase();
    localStorage.setItem(MANUAL_KEY, normalized);
    setManualWallet(normalized);
    await fetchPreferences(normalized);
  }, []);

  // ── Disconnect ────────────────────────────────────────────────────────
  const disconnect = useCallback(() => {
    if (activeWallet) thirdwebDisconnect(activeWallet);
    localStorage.removeItem(MANUAL_KEY);
    setManualWallet(null);
    setPreferences(null);
    setWalletReady(false);
  }, [activeWallet, thirdwebDisconnect]);

  // ── Save preferences ──────────────────────────────────────────────────
  const savePreferences = useCallback(async (prefs) => {
    if (!walletAddress) return;
    try {
      const updated = await updateUserPreferences(walletAddress, prefs);
      setPreferences(updated);
      return updated;
    } catch (err) {
      console.error('Failed to save preferences:', err);
      throw err;
    }
  }, [walletAddress]);

  const applyThemeFromDB = useCallback((themeSetter) => {
    if (preferences?.theme) themeSetter(preferences.theme);
  }, [preferences]);

  return (
    <WalletContext.Provider value={{
      walletAddress,
      walletReady,
      preferences,
      isConnected: !!walletAddress,
      isThirdweb: !!thirdwebAccount,
      connectManual,
      disconnect,
      savePreferences,
      applyThemeFromDB,
      notifications,
      unreadCount,
      markAllRead,
    }}>
      {children}
    </WalletContext.Provider>
  );
}

export function useWallet() {
  const context = useContext(WalletContext);
  if (!context) throw new Error('useWallet must be used within a WalletProvider');
  return context;
}
